import { Deserializer, makeQueryStringSerializer } from 'core/lib/serializer';
import { toFormData } from 'core/util/objects';
import { indexOf, omit, without } from 'lodash';

import { replaceIndex } from 'core/util/arrays';
import apiFetch from './apiFetch';
import { API_URL } from './constants';
import * as Types from './definitions';
import * as Enums from './enums';
import RewardStatus from './enums/RewardStatus';

const baseUrl = (clientId: string, programId: string): string => {
    return `${API_URL}/api/v1/client/${clientId}/program/${programId}/Claim`;
};

const defaultRequest: Partial<Types.ClaimsSearchRequest> = {
    page: 1,
    sortIndex: 'submissionDate',
    sortDescending: true,
};
const serializer = makeQueryStringSerializer<Types.ClaimsSearchRequest>(defaultRequest, {
    page: Deserializer.Int,
    sortDescending: Deserializer.Bool,
});

const CUSTOMER_TOKEN = 'customer';

export default class Claim {
    static defaultRequest = defaultRequest;

    static serializeRequest = serializer.serialize;

    static deserializeRequest = serializer.deserialize;

    static reconciliationHappyPath: Enums.ReconciliationStatus[] = [
        Enums.ReconciliationStatus.PendingSubmission,
        Enums.ReconciliationStatus.PendingValidation,
        Enums.ReconciliationStatus.Validated,
        Enums.ReconciliationStatus.Fulfilled,
        Enums.ReconciliationStatus.Executing,
    ];

    static happyPath: Enums.ClaimStatus[] = [
        Enums.ClaimStatus.Submitted,
        Enums.ClaimStatus.PendingValidation,
        Enums.ClaimStatus.Validated,
        Enums.ClaimStatus.PendingExport,
        Enums.ClaimStatus.Exported,
        Enums.ClaimStatus.Mailed,
        Enums.ClaimStatus.Executing,
        Enums.ClaimStatus.Fulfilled,
    ];

    static isStatusSameOrAfter(status: Enums.ClaimStatus, otherStatus: Enums.ClaimStatus): boolean {
        const statusIndex = Claim.happyPath.indexOf(status);
        const otherStatusIndex = Claim.happyPath.indexOf(otherStatus);
        if (statusIndex === -1 && otherStatusIndex === -1) {
            return status === otherStatus;
        }
        return statusIndex >= otherStatusIndex;
    }

    static isReconciliationStatusSameOrAfter(
        status: Enums.ReconciliationStatus,
        otherStatus: Enums.ReconciliationStatus,
    ): boolean {
        const statusIndex = Claim.reconciliationHappyPath.indexOf(status);
        const otherStatusIndex = Claim.reconciliationHappyPath.indexOf(otherStatus);
        if (statusIndex === -1 && otherStatusIndex === -1) {
            return status === otherStatus;
        }
        return statusIndex >= otherStatusIndex;
    }

    static getManuallyEnterableStatuses(
        status: Enums.ClaimStatus,
        promotionType: Enums.PromotionType,
        overrideManualValidation: boolean,
    ): Enums.ClaimStatus[] {
        switch (status) {
            case Enums.ClaimStatus.Submitted:
                return overrideManualValidation
                    ? [Enums.ClaimStatus.Validated, Enums.ClaimStatus.Disqualified, Enums.ClaimStatus.Incomplete]
                    : [
                          Enums.ClaimStatus.PendingValidation,
                          Enums.ClaimStatus.Disqualified,
                          Enums.ClaimStatus.Incomplete,
                      ];
            case Enums.ClaimStatus.ConfirmingReferral:
                return [Enums.ClaimStatus.Disqualified, Enums.ClaimStatus.PendingRedemption];
            case Enums.ClaimStatus.PendingRedemption:
                return [Enums.ClaimStatus.Disqualified, Enums.ClaimStatus.Incomplete];
            case Enums.ClaimStatus.PendingValidation: {
                let nextStateOptions = [
                    Enums.ClaimStatus.Incomplete,
                    Enums.ClaimStatus.Disqualified,
                    Enums.ClaimStatus.Validated,
                    Enums.ClaimStatus.NeedsReview,
                ];

                if (promotionType === Enums.PromotionType.Redemption) {
                    nextStateOptions = [...nextStateOptions, Enums.ClaimStatus.PendingRedemption];
                }

                return nextStateOptions;
            }
            case Enums.ClaimStatus.Validated: {
                let nextStateOptions = [];
                if (promotionType === Enums.PromotionType.Redemption) {
                    nextStateOptions = overrideManualValidation
                        ? [
                              Enums.ClaimStatus.NeedsReview,
                              Enums.ClaimStatus.Disqualified,
                              Enums.ClaimStatus.PendingRedemption,
                          ]
                        : [
                              Enums.ClaimStatus.NeedsReview,
                              Enums.ClaimStatus.Disqualified,
                              Enums.ClaimStatus.PendingRedemption,
                              Enums.ClaimStatus.PendingValidation,
                          ];
                } else {
                    nextStateOptions = overrideManualValidation
                        ? [Enums.ClaimStatus.NeedsReview, Enums.ClaimStatus.Disqualified]
                        : [
                              Enums.ClaimStatus.PendingValidation,
                              Enums.ClaimStatus.NeedsReview,
                              Enums.ClaimStatus.Disqualified,
                          ];
                }

                return nextStateOptions;
            }
            case Enums.ClaimStatus.Incomplete: {
                let nextStateOptions = [];

                if (promotionType === Enums.PromotionType.Redemption) {
                    nextStateOptions = [
                        Enums.ClaimStatus.Disqualified,
                        Enums.ClaimStatus.PendingRedemption,
                        Enums.ClaimStatus.NeedsReview,
                    ];
                } else {
                    nextStateOptions = overrideManualValidation
                        ? [Enums.ClaimStatus.Disqualified, Enums.ClaimStatus.Validated, Enums.ClaimStatus.NeedsReview]
                        : [
                              Enums.ClaimStatus.PendingValidation,
                              Enums.ClaimStatus.Disqualified,
                              Enums.ClaimStatus.Validated,
                              Enums.ClaimStatus.NeedsReview,
                          ];
                }
                return nextStateOptions;
            }
            case Enums.ClaimStatus.Disqualified: {
                let nextStateOptions = [];

                if (promotionType === Enums.PromotionType.Redemption) {
                    nextStateOptions = [
                        Enums.ClaimStatus.Incomplete,
                        Enums.ClaimStatus.PendingRedemption,
                        Enums.ClaimStatus.NeedsReview,
                    ];
                } else {
                    nextStateOptions = overrideManualValidation
                        ? [Enums.ClaimStatus.Validated, Enums.ClaimStatus.Incomplete, Enums.ClaimStatus.NeedsReview]
                        : [
                              Enums.ClaimStatus.PendingValidation,
                              Enums.ClaimStatus.Validated,
                              Enums.ClaimStatus.Incomplete,
                              Enums.ClaimStatus.NeedsReview,
                          ];
                }
                return nextStateOptions;
            }
            case Enums.ClaimStatus.PotentialDuplicate:
                return [Enums.ClaimStatus.Disqualified, Enums.ClaimStatus.Validated];

            case Enums.ClaimStatus.NeedsReview: {
                let nextStateOptions = [];

                if (promotionType === Enums.PromotionType.Redemption) {
                    nextStateOptions = [
                        Enums.ClaimStatus.Incomplete,
                        Enums.ClaimStatus.PendingRedemption,
                        Enums.ClaimStatus.Disqualified,
                    ];
                } else {
                    nextStateOptions = overrideManualValidation
                        ? [Enums.ClaimStatus.Validated, Enums.ClaimStatus.Incomplete, Enums.ClaimStatus.Disqualified]
                        : [
                              Enums.ClaimStatus.PendingValidation,
                              Enums.ClaimStatus.Validated,
                              Enums.ClaimStatus.Incomplete,
                              Enums.ClaimStatus.Disqualified,
                          ];
                }
                return nextStateOptions;
            }
            case Enums.ClaimStatus.PendingExport:
            case Enums.ClaimStatus.Exported:
            case Enums.ClaimStatus.Mailed:
            case Enums.ClaimStatus.Issued:
            case Enums.ClaimStatus.Executing:
            case Enums.ClaimStatus.Fulfilled:
                return [];
        }
    }

    static getManuallyEnterableReconciliationStatuses(
        status: Enums.ReconciliationStatus,
    ): Enums.ReconciliationStatus[] {
        return without(
            [
                Enums.ReconciliationStatus.PendingValidation,
                Enums.ReconciliationStatus.Incomplete,
                Enums.ReconciliationStatus.Disqualified,
                Enums.ReconciliationStatus.Validated,
            ],
            status,
        );
    }

    static getCreator(claimDetail: Types.ClaimDetail): string {
        if (!claimDetail.createdBy) {
            return CUSTOMER_TOKEN;
        }
        return `${claimDetail.createdBy.firstName} ${claimDetail.createdBy.lastName}`;
    }

    static updateClaimReward(
        onClaimChange: (claimChanges: any) => void,
        claim: any,
        promotion: any,
        parentChoiceCalcId: number | null,
        selectedRewardCalcId: number,
    ) {
        if (!promotion.rewardCalculations) return;

        const [parentChoiceRewardCalc] = promotion.rewardCalculations.filter(
            (r: Types.RewardCalculation) => r.id === parentChoiceCalcId,
        );

        if (!parentChoiceRewardCalc) return null;

        const [selectedRewardedCalculation] = parentChoiceRewardCalc.choices.filter(
            (c: Types.RewardCalculation) => c.id === selectedRewardCalcId,
        );

        if (!selectedRewardedCalculation) return null;

        // template for a new reward
        let updatedReward: Types.Reward = {
            rewardCalculation: selectedRewardedCalculation,
            claimId: claim.id,
            cardRewardDetail: null,
            rewardStatusHistory: [],
            status: Enums.RewardStatus.Inactive as Enums.RewardStatus | null,
            validatedAmount: 0,
            id: null,
            rewardPackage: selectedRewardedCalculation.rewardPackages[0],
            externalReference1: null,
            externalReference2: null,
            externalReference3: null,
        };

        let foundReward = false;

        // see if the parentRewardCalc Choices contains any reward calcs in the current claim rewards set.
        (claim.rewards || []).forEach((reward: Types.Reward) => {
            const [foundCalc] = parentChoiceRewardCalc.choices.filter(
                (c: Types.RewardCalculation) => c.id === reward.rewardCalculation.id,
            );
            if (foundCalc) {
                foundReward = true;
                updatedReward = reward;
            }
        });

        // the case where no matching reward was found after a selection
        if (!foundReward) {
            onClaimChange({
                ...claim,
                rewards: [...(claim.rewards || []), updatedReward],
            });
        } else {
            if (!claim.rewards || !selectedRewardedCalculation) return null;

            const rewardToUpdateIndex = claim.rewards.indexOf(updatedReward);

            // should only be 1 since we're dealing with choice scenarios that don't have multiple rewards
            const [firstRewardPackage] = selectedRewardedCalculation.rewardPackages;
            if (firstRewardPackage) {
                updatedReward.rewardPackage = firstRewardPackage;
            }

            const updatedRewards = [
                ...claim.rewards.slice(0, rewardToUpdateIndex),
                { ...updatedReward, rewardCalculation: selectedRewardedCalculation },
                ...claim.rewards.slice(rewardToUpdateIndex + 1),
            ];

            onClaimChange({ ...claim, rewards: updatedRewards });
        }
    }

    static getSelectedClaimReward(choiceRewardCalc: Types.ChoiceRewardCalculation, rewards: Types.Reward[] | null) {
        if (rewards?.length === 0) {
            return null;
        }
        let selectedRewardId: number | null = null;

        rewards?.forEach((r: Types.Reward) => {
            const foundId = choiceRewardCalc.choices.find((c) => c.id === r.rewardCalculation.id)?.id;
            if (foundId) {
                selectedRewardId = foundId;
            }
        });
        return selectedRewardId;
    }

    static getChangeAuthor(claimDetail: Types.ClaimDetail): string {
        if (!claimDetail.changedBy) {
            return CUSTOMER_TOKEN;
        }
        return `${claimDetail.changedBy.firstName} ${claimDetail.changedBy.lastName}`;
    }

    static async fetchSingle(clientId: string, programId: string, claimId: string): Promise<Types.ClaimDetail> {
        const response = await apiFetch(`${API_URL}/api/v1/client/${clientId}/program/${programId}/Claim/${claimId}`);
        const claim: Types.ClaimDetail = await response.json();
        return claim;
    }

    static async fetchAll(clientId: string, programId: string, customerId: string): Promise<Types.ClaimDetail[]> {
        const response = await apiFetch(`${baseUrl(clientId, programId)}/customer/${customerId}`);
        const claims: Types.ClaimDetail[] = await response.json();
        return claims;
    }

    static async fetchAllPendingValidation(
        clientId: string,
        programId: string,
        claimId?: string,
    ): Promise<Types.ValidationQueueClaim> {
        const queryString = claimId ? `?claimId=${claimId}` : '';
        const response = await apiFetch(`${baseUrl(clientId, programId)}/validation${queryString}`);
        const claim: Types.ValidationQueueClaim = await response.json();
        return claim;
    }

    static async fetchDuplicateFlags(
        clientId: string,
        programId: string,
        claimId: string,
    ): Promise<Types.DuplicateFlag[]> {
        const response = await apiFetch(
            `${API_URL}/api/v1/client/${clientId}/program/${programId}/Duplicate/${claimId}`,
        );
        const duplicateFlags: Types.DuplicateFlag[] = await response.json();
        return duplicateFlags;
    }

    static async updateDuplicateFlags(
        clientId: string,
        programId: string,
        claimId: string,
        duplicateStatus: Types.DuplicateStatusUpdateRequest,
    ): Promise<void> {
        await apiFetch(`${API_URL}/api/v1/client/${clientId}/program/${programId}/Duplicate/${claimId}`, {
            headers: [['Content-Type', 'application/json']],
            method: 'PUT',
            body: JSON.stringify(duplicateStatus),
        });
    }

    static async search(
        clientId: string,
        programId: string,
        claimsSearchRequest: Partial<Types.ClaimsSearchRequest>,
        batchId?: string,
    ): Promise<Types.SearchResultsResponse<Types.ClaimDetail>> {
        const batchSearchPath = batchId ? `/batch/${batchId}` : '';
        const query = Claim.serializeRequest(claimsSearchRequest, true);
        const response = await apiFetch(`${baseUrl(clientId, programId)}${batchSearchPath}?${query}`);
        const claims: Types.SearchResultsResponse<Types.ClaimDetail> = await response.json();
        return claims;
    }

    static async searchCompact(
        clientId: string,
        programId: string,
        claimsSearchRequest: Partial<Types.ClaimsSearchRequest>,
        batchId?: string,
    ): Promise<Types.SearchResultsResponse<Types.CompactClaimDetail>> {
        const batchSearchPath = batchId ? `/batchClaims/${batchId}` : '';
        const query = Claim.serializeRequest(claimsSearchRequest, true);
        const response = await apiFetch(`${baseUrl(clientId, programId)}${batchSearchPath}?${query}`);
        const claims: Types.SearchResultsResponse<Types.CompactClaimDetail> = await response.json();
        return claims;
    }

    static async create(clientId: string, programId: string, claim: Types.Claim): Promise<string> {
        const postClaim = omit(claim, 'id');
        const response = await apiFetch(`${baseUrl(clientId, programId)}`, {
            method: 'POST',
            body: toFormData(postClaim),
        });
        return response.json();
    }

    static async update(clientId: string, programId: string, claim: Types.Claim): Promise<void> {
        await apiFetch(`${baseUrl(clientId, programId)}`, {
            method: 'PUT',
            body: toFormData(claim),
        });
    }

    static async processRedemption(clientId: string, programId: string, claim: Types.Claim): Promise<void> {
        await apiFetch(`${baseUrl(clientId, programId)}/processRedemption`, {
            method: 'PUT',
            body: toFormData(claim),
        });
    }

    static async sendReminderEmail(clientId: string, programId: string, claimId: string): Promise<void> {
        await apiFetch(`${baseUrl(clientId, programId)}/reminderNotification/${claimId}`, {
            method: 'PUT',
        });
    }

    static async fetchHistory(clientId: string, programId: string, claimId: string): Promise<Types.ClaimHistory[]> {
        const response = await apiFetch(`${baseUrl(clientId, programId)}/${claimId}/history`);
        const json = await response.json();
        return json;
    }

    static async searchByCardReward(
        clientId: string,
        programId: string,
        cardRewardDetailId: string,
        claimsSearchRequest: Partial<Types.ClaimsSearchRequest>,
    ): Promise<Types.SearchResultsResponse<Types.ClaimDetail>> {
        const query = Claim.serializeRequest(claimsSearchRequest, true);
        const response = await apiFetch(`${baseUrl(clientId, programId)}/cardReward/${cardRewardDetailId}?${query}`);
        const json = await response.json();
        return json;
    }

    static async searchByReward(
        clientId: string,
        programId: string,
        rewardId: string,
        claimsSearchRequest: Partial<Types.ClaimsSearchRequest>,
    ): Promise<Types.SearchResultsResponse<Types.ClaimDetail>> {
        const query = Claim.serializeRequest(claimsSearchRequest, true);
        const response = await apiFetch(`${baseUrl(clientId, programId)}/reward/${rewardId}?${query}`);
        const json = await response.json();
        return json;
    }
}
